import React, { useState } from 'react';
import { XMarkIcon, CheckIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import Button from '../UI/Button';
import adminService from '../../api/services/admin.service';
import {
  ADMIN_ROLES,
  ADMIN_ROLE_LABELS,
  AdminRole,
} from '../../constants/adminRoles';

interface AddAdminModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface FormData {
  fullName: string;
  username: string;
  phoneNumber: string;
  email: string;
  role: AdminRole;
  password: string;
  confirmPassword: string;
}

interface SuccessData {
  username: string;
  password: string;
  fullName: string;
}

const AddAdminModal: React.FC<AddAdminModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    username: '',
    phoneNumber: '',
    email: '',
    role: 'admin',
    password: '',
    confirmPassword: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successData, setSuccessData] = useState<SuccessData | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === 'password' || name === 'confirmPassword') {
      setPasswordError('');
    }
  };

  const validatePassword = (password: string): boolean => {
    if (password.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return false;
    }
    if (!/[A-Z]/.test(password)) {
      setPasswordError('Password must contain at least one uppercase letter');
      return false;
    }
    if (!/[a-z]/.test(password)) {
      setPasswordError('Password must contain at least one lowercase letter');
      return false;
    }
    if (!/[0-9]/.test(password)) {
      setPasswordError('Password must contain at least one number');
      return false;
    }
    setPasswordError('');
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (formData.password !== formData.confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    if (!validatePassword(formData.password)) {
      return;
    }

    setIsSubmitting(true);

    try {
      const newAdmin = await adminService.create({
        fullName: formData.fullName,
        username: formData.username,
        phoneNumber: formData.phoneNumber,
        email: formData.email,
        role: formData.role,
        password: formData.password,
      });

      setSuccessData({
        username: newAdmin.username,
        password: formData.password,
        fullName: newAdmin.fullName,
      });

      setShowSuccess(true);

      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      console.error('Error creating admin:', err);
      setError(err.response?.data?.message || 'Failed to create admin');
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 3000);
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setShowSuccess(false);
      setFormData({
        fullName: '',
        username: '',
        phoneNumber: '',
        email: '',
        role: 'admin',
        password: '',
        confirmPassword: '',
      });
      setSuccessData(null);
      setCopiedField(null);
      setPasswordError('');
      setError(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  if (showSuccess && successData) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                <CheckIcon className="w-5 h-5 text-green-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">Admin Added Successfully!</h2>
            </div>
            <button onClick={handleClose} className="p-1 rounded-lg hover:bg-gray-100 transition-colors">
              <XMarkIcon className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <p className="text-sm text-gray-600 mb-3">
            Admin <strong>{successData.fullName}</strong> has been created successfully.
            Here are the login credentials:
          </p>

          <div className="bg-gray-50 rounded-lg p-3 mb-3 border border-gray-200">
            <div className="space-y-2">
              <div>
                <label className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">Username</label>
                <div className="flex items-center justify-between mt-0.5">
                  <code className="text-sm font-mono font-semibold text-gray-900">{successData.username}</code>
                  <button
                    onClick={() => copyToClipboard(successData.username, 'username')}
                    className="flex items-center gap-1 text-xs text-secondary-500 hover:text-secondary-600 transition-colors"
                  >
                    <DocumentDuplicateIcon className="w-3.5 h-3.5" />
                    {copiedField === 'username' ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>
              <div className="border-t border-gray-200 pt-2">
                <label className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">Password</label>
                <div className="flex items-center justify-between mt-0.5">
                  <code className="text-sm font-mono font-semibold text-gray-900">{successData.password}</code>
                  <button
                    onClick={() => copyToClipboard(successData.password, 'password')}
                    className="flex items-center gap-1 text-xs text-secondary-500 hover:text-secondary-600 transition-colors"
                  >
                    <DocumentDuplicateIcon className="w-3.5 h-3.5" />
                    {copiedField === 'password' ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2.5 mb-3">
            <p className="text-xs text-yellow-800">
              <strong>Note:</strong> Please save these credentials. The password cannot be recovered.
            </p>
          </div>

          <div className="flex gap-3">
            <Button variant="secondary" onClick={handleClose} size="sm" className="flex-1">
              Close
            </Button>
            <Button size="sm" onClick={() => {
              const credentials = `Username: ${successData.username}\nPassword: ${successData.password}`;
              copyToClipboard(credentials, 'both');
            }} className="flex-1">
              Copy All
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-3.5 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-lg font-bold text-gray-900">Add Admin</h2>
          <button
            onClick={handleClose}
            className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
            disabled={isSubmitting}
          >
            <XMarkIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                required
                placeholder="Enter full name"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Username <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                required
                placeholder="Enter username"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors text-sm"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                required
                placeholder="Enter phone number"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder="Enter email address"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role <span className="text-red-500">*</span>
            </label>
            <select
              name="role"
              value={formData.role}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors appearance-none bg-white text-sm"
            >
              {ADMIN_ROLES.map((role) => (
                <option key={role} value={role}>
                  {ADMIN_ROLE_LABELS[role]}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter password"
                  className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirm Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  required
                  placeholder="Confirm password"
                  className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>
          </div>

          {passwordError && (
            <p className="text-xs text-red-500">{passwordError}</p>
          )}

          <div className="text-xs text-gray-500">
            Password must be at least 6 characters with uppercase, lowercase, and numbers
          </div>

          <div className="flex gap-3 pt-3 border-t border-gray-200">
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
              disabled={isSubmitting}
              size="sm"
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              size="sm"
              className="flex-1"
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Adding...
                </span>
              ) : (
                'Add Admin'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddAdminModal;